import React, { useState } from 'react';
import { useTranslate } from '../components/TranslationText';
import { toast } from 'react-toastify';

const LOGO_URL = 'https://eahs.ae/assets/img/EAHSlogo.svg';

const POLL_SECTIONS = [
    {
        titleKey: 'web.personalInformation',
        titleDefault: 'Personal Information',
        type: 'info',
        questions: [
            { id: 1, labelKey: 'web.name', labelDefault: 'Name', inputType: 'text' },
            { id: 2, labelKey: 'web.mobileNumber', labelDefault: 'Mobile Number', inputType: 'tel' },
            { id: 3, labelKey: 'web.email', labelDefault: 'Email', inputType: 'email' },
        ],
    },
    {
        titleKey: 'web.horsesStables',
        titleDefault: 'Horses Stables',
        type: 'rating',
        questions: [
            { id: 4, labelKey: 'web.stables', labelDefault: 'Stables' },
            { id: 5, labelKey: 'web.horsesEntry', labelDefault: 'Horses Entry' },
            { id: 6, labelKey: 'web.horseShower', labelDefault: 'Horse Shower' },
        ],
    },
    {
        titleKey: 'web.arena',
        titleDefault: 'Arena',
        type: 'rating',
        questions: [
            { id: 7, labelKey: 'web.heatingArea', labelDefault: 'Heating Area' },
            { id: 8, labelKey: 'web.horseEntry', labelDefault: 'Horse Entry' },
            { id: 9, labelKey: 'web.arenaQuestion', labelDefault: 'Arena' },
            { id: 10, labelKey: 'web.vipArea', labelDefault: 'VIP Area' },
            { id: 11, labelKey: 'web.services', labelDefault: 'Services' },
            { id: 12, labelKey: 'web.handlersTent', labelDefault: 'Handlers Tent' },
        ],
    },
    {
        titleKey: 'web.officialList',
        titleDefault: 'Official List',
        type: 'rating',
        questions: [
            { id: 13, labelKey: 'web.judges', labelDefault: 'Judges' },
            { id: 14, labelKey: 'web.ringMasters', labelDefault: 'Ring Masters' },
            { id: 15, labelKey: 'web.technical', labelDefault: 'Technical' },
            { id: 16, labelKey: 'web.dc', labelDefault: 'DC' },
        ],
    },
    {
        titleKey: 'web.satisfaction',
        titleDefault: 'Satisfaction',
        type: 'satisfaction',
        questions: [
            {
                id: 17,
                labelKey: 'web.judgingPanelSatisfaction',
                labelDefault: 'Your satisfaction with the mechanism for selecting the judging panel for classes and championships',
            },
        ],
    },
    {
        titleKey: 'web.commentsAndSuggestions',
        titleDefault: 'Comments & Suggestions',
        type: 'comments',
        questions: [
            {
                id: 18,
                labelKey: 'web.commentsForCommittee',
                labelDefault: 'Comments and suggestions for the organizing committee',
            },
        ],
    },
];

const RATING_OPTIONS = [
    { value: 'excellent', labelKey: 'web.excellent', labelDefault: 'Excellent', icon: 'fa-face-grin-stars', color: '#27ae60' },
    { value: 'very_good', labelKey: 'web.veryGood', labelDefault: 'Very Good', icon: 'fa-face-smile', color: '#2ecc71' },
    { value: 'good', labelKey: 'web.good', labelDefault: 'Good', icon: 'fa-face-meh', color: '#f39c12' },
    { value: 'needs_improvements', labelKey: 'web.needsImprovements', labelDefault: 'Needs Improvements', icon: 'fa-face-frown', color: '#e74c3c' },
];

const SATISFACTION_OPTIONS = [
    { value: 'agree', labelKey: 'web.agree', labelDefault: 'Agree', icon: 'fa-thumbs-up', color: '#27ae60' },
    { value: 'disagree', labelKey: 'web.disagree', labelDefault: 'Disagree', icon: 'fa-thumbs-down', color: '#e74c3c' },
    { value: 'neutral', labelKey: 'web.neutral', labelDefault: 'Neutral', icon: 'fa-hand', color: '#f39c12' },
];

function ChoiceButton({ option, selected, onClick, translate }) {
    const isSelected = selected === option.value;
    const activeColor = option.color || '#E44D52';
    
    return (
        <div
            className="poll-choice-btn"
            onClick={onClick}
            style={{
                flex: '1 1 0',
                minWidth: 120,
                padding: '12px 16px',
                borderRadius: 10,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                border: isSelected ? `2px solid ${activeColor}` : '2px solid #e8e8e8',
                backgroundColor: isSelected ? `${activeColor}10` : '#fff',
                transition: 'all 0.2s ease',
            }}
        >
            <div style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: isSelected ? `5px solid ${activeColor}` : '2px solid #ccc',
                marginRight: 10,
                transition: 'all 0.2s ease',
            }}></div>
            <span
                style={{
                    fontSize: 14,
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? activeColor : '#555',
                    transition: 'all 0.2s ease',
                }}
            >
                {translate(option.labelKey, option.labelDefault)}
            </span>
        </div>
    );
}

function StepIndicator({ totalSteps, currentStep }) {
    return (
        <div className="d-flex align-items-center justify-content-center gap-1 mb-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                    key={i}
                    style={{
                        width: i === currentStep ? 28 : 10,
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: i === currentStep ? '#E44D52' : i < currentStep ? '#E44D52' : '#ddd',
                        opacity: i < currentStep ? 0.5 : 1,
                        transition: 'all 0.3s ease',
                    }}
                />
            ))}
        </div>
    );
}

export default function PollPage() {
    const { translate } = useTranslate();
    const [answers, setAnswers] = useState({});
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [slideDirection, setSlideDirection] = useState('');

    const totalSteps = POLL_SECTIONS.length;

    const handleTextChange = (id, value) => {
        setAnswers(prev => ({ ...prev, [id]: value }));
        if (errors[id]) setErrors(prev => ({ ...prev, [id]: false }));
    };

    const handleRadioChange = (id, value) => {
        setAnswers(prev => ({ ...prev, [id]: value }));
        if (errors[id]) setErrors(prev => ({ ...prev, [id]: false }));
    };

    const validateCurrentStep = () => {
        const section = POLL_SECTIONS[currentStep];
        const newErrors = {};
        let hasError = false;

        section.questions.forEach(q => {
            const val = answers[q.id];
            if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) {
                newErrors[q.id] = true;
                hasError = true;
            }
        });

        if (hasError) {
            setErrors(prev => ({ ...prev, ...newErrors }));
            toast.error(translate('web.pleaseAnswerAll', 'Please answer all questions before proceeding.'));
            return false;
        }
        return true;
    };

    const goNext = () => {
        if (!validateCurrentStep()) return;
        if (currentStep < totalSteps - 1) {
            setSlideDirection('slide-left');
            setTimeout(() => {
                setCurrentStep(prev => prev + 1);
                setSlideDirection('slide-in-right');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 200);
        }
    };

    const goPrev = () => {
        if (currentStep > 0) {
            setSlideDirection('slide-right');
            setTimeout(() => {
                setCurrentStep(prev => prev - 1);
                setSlideDirection('slide-in-left');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 200);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateCurrentStep()) return;
        localStorage.setItem('eahs_poll_answers', JSON.stringify(answers));
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (submitted) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="text-center py-5 px-4">
                    <div
                        style={{
                            width: 90,
                            height: 90,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 24px',
                        }}
                    >
                        <i className="fa-solid fa-check" style={{ fontSize: 40, color: '#fff' }}></i>
                    </div>
                    <h2 className="fw-bold mb-3" style={{ color: '#222' }}>
                        {translate('web.thankYou', 'Thank You!')}
                    </h2>
                    <p className="text-muted mb-4" style={{ fontSize: 16, lineHeight: 1.7 }}>
                        {translate(
                            'web.pollSubmittedMessage',
                            'Your feedback has been submitted successfully. We appreciate your time and valuable input.'
                        )}
                    </p>
                    <button
                        className="btn px-5 py-2 fw-bold"
                        style={{ backgroundColor: '#E44D52', color: '#fff', borderRadius: 8, border: 'none' }}
                        onClick={() => window.close()}
                    >
                        {translate('web.close', 'Close')}
                    </button>
                </div>
            </div>
        );
    }

    const section = POLL_SECTIONS[currentStep];
    const isLastStep = currentStep === totalSteps - 1;
    const isFirstStep = currentStep === 0;

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
            {/* Logo Header */}
            <div
                style={{
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #eee',
                    padding: '12px 0',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                }}
            >
                <div className="container">
                    <div className="d-flex align-items-center justify-content-center">
                        <img src={LOGO_URL} alt="EAHS Logo" style={{ height: 50 }} />
                    </div>
                </div>
            </div>

            {/* Slider Content */}
            <div className="container py-4" style={{ maxWidth: 900 }}>
                {/* Title Card */}
                <div 
                    className="mb-4 text-center py-4 px-3" 
                    style={{ 
                        backgroundColor: '#E44D52', 
                        color: 'white', 
                        borderRadius: 12,
                        boxShadow: '0 4px 15px rgba(228, 77, 82, 0.2)'
                    }}
                >
                    <h3 className="fw-bold mb-2">
                        {translate('web.surveyTitle', "Survey Al SHIRAA'A International Show 2026")}
                    </h3>
                    <p className="mb-0" style={{ fontSize: 14, opacity: 0.9 }}>
                        {translate('web.surveySubtitle', "Help us improve the organization of Arabian horse championships")}
                    </p>
                </div>

                <StepIndicator totalSteps={totalSteps} currentStep={currentStep} />

                <div style={{ position: 'relative', overflow: 'hidden' }}>
                    <div
                        key={currentStep}
                        className={`poll-slide ${slideDirection}`}
                    >
                        {/* Section Card */}
                        <div
                            className="bg-white"
                            style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 8px rgba(0,0,0,0.05)' }}
                        >
                            {/* Section Header */}
                            <div
                                className="px-4 py-3"
                                style={{ borderBottom: '3px solid #E44D52', backgroundColor: '#fafafa' }}
                            >
                                <h5 className="fw-bold mb-0" style={{ color: '#333', fontSize: 17 }}>
                                    <i className="fa-solid fa-circle me-2" style={{ fontSize: 8, color: '#E44D52', verticalAlign: 'middle' }}></i>
                                    {translate(section.titleKey, section.titleDefault)}
                                    <span className="float-end fw-normal text-muted" style={{ fontSize: 13 }}>
                                        {currentStep + 1} / {totalSteps}
                                    </span>
                                </h5>
                            </div>

                            <div className="px-4 py-4">
                                {section.type === 'info' && section.questions.map((q) => (
                                    <div key={q.id} className="mb-3">
                                        <label className="form-label fw-bold text-dark" style={{ fontSize: 14 }}>
                                            {q.id}. {translate(q.labelKey, q.labelDefault)}
                                            {errors[q.id] && <span className="text-danger ms-2" style={{ fontSize: 12 }}>* {translate('web.required', 'Required')}</span>}
                                        </label>
                                        <input
                                            type={q.inputType}
                                            className="form-control"
                                            style={{ borderRadius: 8, border: errors[q.id] ? '1px solid #dc3545' : '1px solid #ddd', padding: '10px 14px' }}
                                            placeholder={translate('web.enterYourAnswer', 'Enter your answer')}
                                            value={answers[q.id] || ''}
                                            onChange={(e) => handleTextChange(q.id, e.target.value)}
                                        />
                                    </div>
                                ))}

                                {section.type === 'rating' && section.questions.map((q) => (
                                    <div key={q.id} className="mb-4 pb-3" style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <label className="form-label fw-bold text-dark mb-3" style={{ fontSize: 14 }}>
                                            {q.id}. {translate(q.labelKey, q.labelDefault)}
                                            {errors[q.id] && <span className="text-danger ms-2" style={{ fontSize: 12 }}>* {translate('web.required', 'Required')}</span>}
                                        </label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {RATING_OPTIONS.map((opt) => (
                                                <ChoiceButton
                                                    key={opt.value}
                                                    option={opt}
                                                    selected={answers[q.id]}
                                                    onClick={() => handleRadioChange(q.id, opt.value)}
                                                    translate={translate}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {section.type === 'satisfaction' && section.questions.map((q) => (
                                    <div key={q.id} className="mb-3">
                                        <label className="form-label fw-bold text-dark mb-3" style={{ fontSize: 14, lineHeight: 1.5 }}>
                                            {q.id}. {translate(q.labelKey, q.labelDefault)}
                                            {errors[q.id] && <span className="text-danger ms-2" style={{ fontSize: 12 }}>* {translate('web.required', 'Required')}</span>}
                                        </label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {SATISFACTION_OPTIONS.map((opt) => (
                                                <ChoiceButton
                                                    key={opt.value}
                                                    option={opt}
                                                    selected={answers[q.id]}
                                                    onClick={() => handleRadioChange(q.id, opt.value)}
                                                    translate={translate}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {section.type === 'comments' && section.questions.map((q) => (
                                    <div key={q.id} className="mb-3">
                                        <label className="form-label fw-bold text-dark mb-2" style={{ fontSize: 14 }}>
                                            {q.id}. {translate(q.labelKey, q.labelDefault)}
                                            {errors[q.id] && <span className="text-danger ms-2" style={{ fontSize: 12 }}>* {translate('web.required', 'Required')}</span>}
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows={5}
                                            style={{ borderRadius: 8, border: errors[q.id] ? '1px solid #dc3545' : '1px solid #ddd', padding: '10px 14px', resize: 'vertical' }}
                                            placeholder={translate('web.enterYourAnswer', 'Enter your answer')}
                                            value={answers[q.id] || ''}
                                            onChange={(e) => handleTextChange(q.id, e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between align-items-center mt-4 px-1">
                    <button
                        type="button"
                        className="btn px-4 py-2 fw-bold d-flex align-items-center gap-2"
                        style={{
                            backgroundColor: isFirstStep ? '#e8e8e8' : '#fff',
                            color: isFirstStep ? '#bbb' : '#333',
                            borderRadius: 10,
                            border: isFirstStep ? '1px solid #e8e8e8' : '1px solid #ddd',
                            fontSize: 14,
                            cursor: isFirstStep ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                        }}
                        disabled={isFirstStep}
                        onClick={goPrev}
                    >
                        <i className="fa-solid fa-arrow-left"></i>
                        {translate('web.previous', 'Previous')}
                    </button>

                    {isLastStep ? (
                        <button
                            type="button"
                            className="btn px-5 py-2 fw-bold d-flex align-items-center gap-2"
                            style={{
                                backgroundColor: '#E44D52',
                                color: '#fff',
                                borderRadius: 10,
                                border: 'none',
                                fontSize: 14,
                                boxShadow: '0 4px 15px rgba(228, 77, 82, 0.3)',
                                transition: 'all 0.2s',
                            }}
                            onClick={handleSubmit}
                            onMouseEnter={(e) => { e.target.style.backgroundColor = '#c0392b'; }}
                            onMouseLeave={(e) => { e.target.style.backgroundColor = '#E44D52'; }}
                        >
                            <i className="fa-solid fa-paper-plane"></i>
                            {translate('web.submitSurvey', 'Submit')}
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn px-4 py-2 fw-bold d-flex align-items-center gap-2"
                            style={{
                                backgroundColor: '#E44D52',
                                color: '#fff',
                                borderRadius: 10,
                                border: 'none',
                                fontSize: 14,
                                transition: 'all 0.2s',
                            }}
                            onClick={goNext}
                            onMouseEnter={(e) => { e.target.style.backgroundColor = '#c0392b'; }}
                            onMouseLeave={(e) => { e.target.style.backgroundColor = '#E44D52'; }}
                        >
                            {translate('web.next', 'Next')}
                            <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    )}
                </div>
            </div>

            <style>{`
                .poll-choice-btn:hover {
                    transform: scale(1.05) !important;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.08) !important;
                }
                .poll-slide {
                    animation: pollFadeIn 0.3s ease forwards;
                }
                .slide-left {
                    animation: slideOutLeft 0.2s ease forwards;
                }
                .slide-right {
                    animation: slideOutRight 0.2s ease forwards;
                }
                .slide-in-right {
                    animation: slideInRight 0.3s ease forwards;
                }
                .slide-in-left {
                    animation: slideInLeft 0.3s ease forwards;
                }
                @keyframes pollFadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideOutLeft {
                    from { opacity: 1; transform: translateX(0); }
                    to { opacity: 0; transform: translateX(-30px); }
                }
                @keyframes slideOutRight {
                    from { opacity: 1; transform: translateX(0); }
                    to { opacity: 0; transform: translateX(30px); }
                }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(30px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes slideInLeft {
                    from { opacity: 0; transform: translateX(-30px); }
                    to { opacity: 1; transform: translateX(0); }
                }
            `}</style>
        </div>
    );
}
